import React, { useCallback, useMemo, useState } from 'react';
import { LuCheck, LuPencil, LuTrash2, LuX } from 'react-icons/lu';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import {
  factoryCategoryKind,
  factoryCategoryShort,
  factoryProductIsPosSellable,
  factoryShowsSellingPrice,
} from '../constants/factoryStockCategories';
import { categorySelectOptionsForUser } from '../constants/businessStockCategories';
import { pharmacyExpiryStatus } from '../constants/pharmacyStockCategories';
import { isAutomotiveBusiness, isBrickFactoryUser, isPharmacyUser } from '../utils/businessType';
import { PRODUCT_UNITS, productUnitLabel } from '../constants/productUnits';
import { normalizeRole } from '../utils/userLocale';
import './ProductListPage.css';

function matchesSearch(p, q) {
  if (!q) return true;
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const name = String(p.name || '').toLowerCase();
  const cat = String(p.category || '').toLowerCase();
  const qty = String(p.quantity ?? '');
  const buy = String(p.purchasingPrice ?? '');
  const sell = String(p.sellingPrice ?? '');
  const part = [
    p.partCode,
    p.oemNumber,
    p.vehicleType,
    p.compatibleModels,
    p.brandName,
    p.supplierName,
  ].join(' ').toLowerCase();
  return (
    name.includes(s) ||
    cat.includes(s) ||
    qty.includes(s) ||
    buy.includes(s) ||
    sell.includes(s) ||
    part.includes(s)
  );
}

const ProductListPage = ({ pendingOnly = false }) => {
  const { user } = useAuth();
  const { products, pendingProducts, inventoryLoading, approveProduct, rejectProduct, updateProduct, deleteProduct, refreshProducts } = useProducts();
  const [actionId, setActionId] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    unit: 'pcs',
    quantity: 0,
    purchasingPrice: 0,
    sellingPrice: 0,
    lowStockLimit: 0,
    expiryDate: '',
    batchLot: '',
    partCode: '',
    oemNumber: '',
    vehicleType: '',
    compatibleModels: '',
    brandName: '',
    supplierName: '',
  });

  const role = normalizeRole(user?.role);
  const canModerate = role === 'owner' || role === 'manager';
  const factoryMode = isBrickFactoryUser(user);
  const pharmacyMode = isPharmacyUser(user);
  const automotiveMode = isAutomotiveBusiness(user);
  const automotiveCatalogOnly = automotiveMode && !pendingOnly;
  const categoryOptions = categorySelectOptionsForUser(user);

  const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '—');
  const fmtMoney = (n) => (Number.isFinite(Number(n)) && Number(n) > 0 ? fmt(n) : '—');

  const productShowsSell = (p) =>
    !factoryMode || factoryProductIsPosSellable(p) || factoryShowsSellingPrice(p?.category);

  const categories = useMemo(() => {
    const set = new Set();
    (products || []).forEach((p) => {
      const c = (p.category || '').trim();
      if (c) set.add(c);
    });
    (pendingProducts || []).forEach((req) => {
      const c = String(req.product?.category || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [products, pendingProducts]);

  const filteredProducts = useMemo(() => {
    const list = products || [];
    return list.filter((p) => {
      if (category !== 'all' && String(p.category || '').trim() !== category) return false;
      return matchesSearch(p, search);
    });
  }, [products, category, search]);

  const filteredPending = useMemo(() => {
    const list = pendingProducts || [];
    return list.filter((req) => {
      const pr = req.product || {};
      if (category !== 'all' && String(pr.category || '').trim() !== category) return false;
      return matchesSearch(pr, search);
    });
  }, [pendingProducts, category, search]);

  const factoryTableHasSell = useMemo(
    () => factoryMode && filteredProducts.some((p) => productShowsSell(p)),
    [factoryMode, filteredProducts]
  );

  const startEdit = useCallback((p) => {
    setEditId(String(p.id));
    setEditForm({
      name: p.name || '',
      category: p.category || '',
      unit: p.unit || 'pcs',
      quantity: Number(p.quantity) || 0,
      purchasingPrice: Number(p.purchasingPrice) || 0,
      sellingPrice: Number(p.sellingPrice) || 0,
      lowStockLimit: Number(p.lowStockLimit) || 0,
      expiryDate: p.expiryDate || '',
      batchLot: p.batchLot || '',
      barcode: p.barcode || '',
      partCode: p.partCode || '',
      oemNumber: p.oemNumber || '',
      vehicleType: p.vehicleType || '',
      compatibleModels: p.compatibleModels || '',
      brandName: p.brandName || '',
      supplierName: p.supplierName || '',
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditId(null);
  }, []);

  const saveEdit = useCallback(
    async (id) => {
      setActionId(id);
      try {
        const payload = { ...editForm };
        if (factoryMode && !factoryShowsSellingPrice(payload.category)) {
          payload.sellingPrice = 0;
        }
        if (automotiveCatalogOnly) {
          const original = (products || []).find((p) => String(p.id) === String(id));
          if (original) {
            payload.quantity = Number(original.quantity) || 0;
            payload.purchasingPrice = Number(original.purchasingPrice) || 0;
            payload.sellingPrice = Number(original.sellingPrice) || 0;
            payload.lowStockLimit = Number(original.lowStockLimit) || 0;
            payload.supplierName = original.supplierName || null;
          }
        }
        await updateProduct(id, payload);
        setEditId(null);
      } catch {
        /* notify from context */
      } finally {
        setActionId(null);
      }
    },
    [updateProduct, editForm, factoryMode, automotiveCatalogOnly, products]
  );

  const removeProduct = useCallback(
    async (p) => {
      if (!window.confirm(`Delete "${p.name}" permanently?`)) return;
      setActionId(p.id);
      try {
        await deleteProduct(p.id, p.name);
        if (String(editId) === String(p.id)) setEditId(null);
      } catch {
        /* notify from context */
      } finally {
        setActionId(null);
      }
    },
    [deleteProduct, editId]
  );

  const handleApprove = useCallback(
    async (id) => {
      setActionId(id);
      try {
        await approveProduct(id, user);
      } catch {
        /* notify from context */
      } finally {
        setActionId(null);
      }
    },
    [approveProduct, user]
  );

  const handleReject = useCallback(
    async (id) => {
      if (!window.confirm('Reject this product request?')) return;
      setActionId(id);
      try {
        await rejectProduct(id);
      } catch {
        /* notify */
      } finally {
        setActionId(null);
      }
    },
    [rejectProduct]
  );

  return (
    <div className={`product-list-root page-bleed${factoryMode ? ' product-list-root--factory' : ''}`}>
      <header className="product-list-head">
        <h2>{factoryMode ? 'Stock' : automotiveCatalogOnly ? 'Parts catalog' : 'Product list'}</h2>
        <button type="button" className="product-list-refresh" onClick={() => refreshProducts()} disabled={inventoryLoading}>
          {inventoryLoading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      {inventoryLoading && (!products || products.length === 0) ? (
        <p className="product-list-loading">Loading inventory…</p>
      ) : null}

      {!pendingOnly && !inventoryLoading && (!products || products.length === 0) ? (
        <p className="product-list-empty">
          {automotiveCatalogOnly
            ? 'No spare parts registered yet. Go to Spare parts → Record spare part.'
            : 'No approved products yet. Add one from Stock → Add product.'}
        </p>
      ) : (
        <>
          <div className="product-list-toolbar">
            <label className="product-list-field product-list-field--grow">
              <span className="product-list-field-label">Search</span>
              <input
                type="search"
                className="product-list-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={factoryMode ? 'Search…' : automotiveCatalogOnly ? 'Name, category, SKU, OEM…' : 'Name, category, qty, price…'}
                aria-label="Search products"
              />
            </label>
            <label className="product-list-field">
              <span className="product-list-field-label">Category</span>
              <select className="product-list-select" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filter by category">
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {(search.trim() || category !== 'all') && (
              <button type="button" className="product-list-clear-filters" onClick={() => { setSearch(''); setCategory('all'); }}>
                Clear filters
              </button>
            )}
          </div>
          {!pendingOnly && (
            <>
              <p className="product-list-count">
                {factoryMode ? (
                  <>
                    <strong>{filteredProducts.length}</strong>
                    {(products || []).length !== filteredProducts.length
                      ? ` / ${(products || []).length}`
                      : ''}{' '}
                    items
                  </>
                ) : (
                  <>
                    Showing <strong>{filteredProducts.length}</strong> of {(products || []).length} products
                  </>
                )}
              </p>
              {filteredProducts.length === 0 ? (
                <p className="product-list-empty product-list-empty--inline">No items match your filters.</p>
              ) : (
                <div className="product-list-table-wrap">
                  <table className={`product-list-table${factoryMode ? ' product-list-table--factory' : ''}`}>
                    <thead>
                      <tr>
                        <th scope="col">{factoryMode ? 'Item' : 'Name'}</th>
                        <th scope="col">{factoryMode ? 'Type' : 'Category'}</th>
                        {!factoryMode ? (
                          <th scope="col" className="product-list-col-unit">
                            Unit
                          </th>
                        ) : null}
                        {!automotiveCatalogOnly ? (
                          <>
                            <th scope="col" className="product-list-num">
                              Qty
                            </th>
                            <th scope="col" className="product-list-num">
                              <span className={factoryMode ? 'product-list-cost-header-left' : ''}>
                                {factoryMode ? 'Cost' : 'Buy'}
                              </span>
                            </th>
                            {(!factoryMode || factoryTableHasSell) && (
                              <th scope="col" className="product-list-num">
                                Sell
                              </th>
                            )}
                            <th scope="col" className="product-list-num">
                              {factoryMode ? 'Min' : 'Alert'}
                            </th>
                          </>
                        ) : null}
                        {pharmacyMode ? (
                          <>
                            <th scope="col">Sellable</th>
                            <th scope="col">Expiry (FEFO)</th>
                          </>
                        ) : null}
                        {canModerate ? (
                          <th scope="col" className="product-list-col-actions">
                            <span className="sr-only">Actions</span>
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((p) => {
                        const editing = String(editId) === String(p.id);
                        const showSell =
                          !factoryMode ||
                          factoryTableHasSell &&
                            (productShowsSell(p) ||
                              (editing && productShowsSell({ category: editForm.category })));
                        const kind = factoryMode ? factoryCategoryKind(p.category) : null;
                        const lowQty =
                          factoryMode &&
                          p.lowStockLimit > 0 &&
                          Number(p.quantity) <= Number(p.lowStockLimit);

                        return (
                          <tr
                            key={p.id}
                            className={editing ? 'product-list-row--edit' : undefined}
                            data-kind={kind || undefined}
                          >
                            <td className="product-list-col-item">
                              {editing ? (
                                <>
                                  <input
                                    className="product-list-inline-input"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                  />
                                  {automotiveMode ? (
                                    <>
                                      <input
                                        className="product-list-inline-input"
                                        placeholder="SKU / part code"
                                        value={editForm.partCode}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, partCode: e.target.value }))}
                                      />
                                      <input
                                        className="product-list-inline-input"
                                        placeholder="OEM number"
                                        value={editForm.oemNumber}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, oemNumber: e.target.value }))}
                                      />
                                      <input
                                        className="product-list-inline-input"
                                        placeholder="Vehicle type"
                                        value={editForm.vehicleType}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, vehicleType: e.target.value }))}
                                      />
                                      <input
                                        className="product-list-inline-input"
                                        placeholder="Brand"
                                        value={editForm.brandName}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, brandName: e.target.value }))}
                                      />
                                      <input
                                        className="product-list-inline-input"
                                        placeholder="Compatible models"
                                        value={editForm.compatibleModels}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, compatibleModels: e.target.value }))}
                                      />
                                    </>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <span className="product-list-item-name">{p.name}</span>
                                  {pharmacyMode && p.barcode ? (
                                    <span className="product-list-barcode">{p.barcode}</span>
                                  ) : null}
                                  {automotiveMode ? (
                                    <span className="product-list-barcode">
                                      {[
                                        p.partCode ? `SKU: ${p.partCode}` : '',
                                        p.oemNumber ? `OEM: ${p.oemNumber}` : '',
                                        p.vehicleType || '',
                                        p.brandName || '',
                                      ].filter(Boolean).join(' • ') || 'Spare part'}
                                    </span>
                                  ) : null}
                                  {automotiveMode && p.compatibleModels ? (
                                    <span className="product-list-batches">Fits: {p.compatibleModels}</span>
                                  ) : null}
                                  {pharmacyMode && Array.isArray(p.batches) && p.batches.length > 0 ? (
                                    <span className="product-list-batches">
                                      {p.batches.map((b) => (
                                        <span
                                          key={b.id}
                                          className={`product-list-batch-chip product-list-batch-chip--${b.expiryStatus || 'none'}`}
                                        >
                                          {b.quantity}× {b.expiryDate || 'no exp'}
                                          {b.batchLot ? ` (${b.batchLot})` : ''}
                                        </span>
                                      ))}
                                    </span>
                                  ) : null}
                                  {factoryMode && p.brickTypeId ? (
                                    <span className="product-list-linked" title="Linked to brick catalog and production">
                                      Production
                                    </span>
                                  ) : null}
                                  {factoryMode && factoryProductIsPosSellable(p) ? (
                                    <span className="product-list-linked product-list-linked--pos" title="Available at POS">
                                      POS
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </td>
                            <td>
                              {editing ? (
                                pharmacyMode || automotiveMode ? (
                                  <select
                                    className="product-list-inline-input"
                                    value={editForm.category}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, category: e.target.value }))
                                    }
                                  >
                                    {categoryOptions.map((c) => (
                                      <option key={c.label + c.value} value={c.value}>
                                        {c.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    className="product-list-inline-input"
                                    value={editForm.category}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, category: e.target.value }))
                                    }
                                  />
                                )
                              ) : factoryMode ? (
                                <span
                                  className={`product-list-tag product-list-tag--${kind || 'other'}`}
                                  title={p.category || ''}
                                >
                                  {factoryCategoryShort(p.category)}
                                </span>
                              ) : (
                                p.category || '—'
                              )}
                            </td>
                            {!factoryMode ? (
                              <td>
                                {editing ? (
                                  <select
                                    className="product-list-inline-input"
                                    value={editForm.unit}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({ ...prev, unit: e.target.value }))
                                    }
                                  >
                                    {PRODUCT_UNITS.map((u) => (
                                      <option key={u.value} value={u.value}>
                                        {u.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  productUnitLabel(p.unit)
                                )}
                              </td>
                            ) : null}
                            {!automotiveCatalogOnly ? (
                              <>
                                <td className="product-list-num">
                                  {editing ? (
                                    <input
                                      className="product-list-inline-input product-list-inline-input--num"
                                      type="number"
                                      min={0}
                                      value={editForm.quantity}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({ ...prev, quantity: Number(e.target.value) || 0 }))
                                      }
                                    />
                                  ) : (
                                    <span className={lowQty ? 'product-list-qty--low' : undefined}>{fmt(p.quantity)}</span>
                                  )}
                                </td>
                                <td className="product-list-num">
                                  {editing ? (
                                    <input
                                      className="product-list-inline-input product-list-inline-input--num"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={editForm.purchasingPrice}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          purchasingPrice: Number(e.target.value) || 0,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <span className="product-list-money">
                                      {fmtMoney(p.purchasingPrice) !== '—' ? (
                                        <span className={factoryMode ? 'product-list-cost-value-left' : ''}>{fmtMoney(p.purchasingPrice)}</span>
                                      ) : (
                                        '—'
                                      )}
                                    </span>
                                  )}
                                </td>
                                {(!factoryMode || factoryTableHasSell) && (
                                  <td className="product-list-num">
                                    {editing ? (
                                      showSell ? (
                                        <input
                                          className="product-list-inline-input product-list-inline-input--num"
                                          type="number"
                                          min={0}
                                          step="0.01"
                                          value={editForm.sellingPrice}
                                          onChange={(e) =>
                                            setEditForm((prev) => ({
                                              ...prev,
                                              sellingPrice: Number(e.target.value) || 0,
                                            }))
                                          }
                                        />
                                      ) : (
                                        <span className="product-list-na">—</span>
                                      )
                                    ) : showSell && productShowsSell(p) ? (
                                      <span className="product-list-money product-list-money--sell">
                                        {fmtMoney(p.sellingPrice) !== '—' ? (
                                          <>
                                            <span className="product-list-money__cur">(Tsh)</span>
                                            {fmtMoney(p.sellingPrice)}
                                          </>
                                        ) : (
                                          '—'
                                        )}
                                      </span>
                                    ) : (
                                      <span className="product-list-na">—</span>
                                    )}
                                  </td>
                                )}
                                <td className="product-list-num">
                                  {editing ? (
                                    <input
                                      className="product-list-inline-input product-list-inline-input--num"
                                      type="number"
                                      min={0}
                                      value={editForm.lowStockLimit}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          lowStockLimit: Number(e.target.value) || 0,
                                        }))
                                      }
                                    />
                                  ) : p.lowStockLimit > 0 ? (
                                    fmt(p.lowStockLimit)
                                  ) : (
                                    <span className="product-list-na">—</span>
                                  )}
                                </td>
                              </>
                            ) : null}
                            {pharmacyMode ? (
                              <td className="product-list-num">
                                {editing ? '—' : (
                                  <span title="Non-expired units available for POS">
                                    {Number(p.sellableQuantity ?? p.quantity) || 0}
                                  </span>
                                )}
                              </td>
                            ) : null}
                            {pharmacyMode ? (
                              <td>
                                {editing ? (
                                  <div className="product-list-pharmacy-edit">
                                    <input
                                      className="product-list-inline-input"
                                      type="date"
                                      value={editForm.expiryDate}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({ ...prev, expiryDate: e.target.value }))
                                      }
                                    />
                                    <input
                                      className="product-list-inline-input"
                                      type="text"
                                      placeholder="Batch"
                                      value={editForm.batchLot}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({ ...prev, batchLot: e.target.value }))
                                      }
                                      maxLength={64}
                                    />
                                  </div>
                                ) : p.expiryDate ? (
                                  <span
                                    className={`product-list-expiry product-list-expiry--${pharmacyExpiryStatus(p.expiryDate)}`}
                                    title={p.batchLot ? `Batch: ${p.batchLot}` : undefined}
                                  >
                                    {p.expiryDate}
                                    {p.batchLot ? (
                                      <span className="product-list-batch">{p.batchLot}</span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="product-list-na">—</span>
                                )}
                              </td>
                            ) : null}
                            {canModerate ? (
                              <td className="product-list-col-actions">
                                <div className="product-list-row-actions">
                                  {editing ? (
                                    <>
                                      <button
                                        type="button"
                                        className="product-list-icon-btn product-list-icon-btn--save"
                                        disabled={String(actionId) === String(p.id)}
                                        onClick={() => saveEdit(p.id)}
                                        aria-label="Save"
                                        title="Save"
                                      >
                                        <LuCheck size={16} />
                                      </button>
                                      <button
                                        type="button"
                                        className="product-list-icon-btn"
                                        onClick={cancelEdit}
                                        aria-label="Cancel"
                                        title="Cancel"
                                      >
                                        <LuX size={16} />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="product-list-icon-btn"
                                        onClick={() => startEdit(p)}
                                        aria-label="Edit"
                                        title="Edit"
                                      >
                                        <LuPencil size={15} />
                                      </button>
                                      <button
                                        type="button"
                                        className="product-list-icon-btn product-list-icon-btn--danger"
                                        disabled={String(actionId) === String(p.id)}
                                        onClick={() => removeProduct(p)}
                                        aria-label="Delete"
                                        title="Delete"
                                      >
                                        <LuTrash2 size={15} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {pendingProducts && pendingProducts.length > 0 ? (
        <section className="product-list-pending">
          <h3 className="product-list-pending-title">{pendingOnly ? 'Stock pending approvals' : `Pending approval (${pendingProducts.length})`}</h3>
          {(search.trim() || category !== 'all') && (
            <p className="product-list-pending-filter-note">
              Matching filters: <strong>{filteredPending.length}</strong> request(s)
            </p>
          )}
          <p className="product-list-pending-hint">
            These were added by staff and are not in the main list until the business owner approves them.
          </p>
          {filteredPending.length === 0 && (search.trim() || category !== 'all') ? (
            <p className="product-list-empty product-list-empty--inline">No pending items match your filters.</p>
          ) : null}
          <ul className="product-list-pending-list">
            {filteredPending.map((req) => {
              const id = req.requestId;
              const pr = req.product || {};
              const busy = String(actionId) === String(id);
              return (
                <li key={id} className="product-list-pending-card">
                  <div className="product-list-pending-meta">
                    <strong>{pr.name}</strong>
                    <span className="product-list-pending-by">{req.submittedBy || 'Unknown'}</span>
                  </div>
                  <div className="product-list-pending-details">
                    {pr.category ? <span>{pr.category}</span> : null}
                    <span>Qty {fmt(pr.quantity)}</span>
                    <span>Sell {fmt(pr.sellingPrice)} TZS</span>
                  </div>
                  {canModerate ? (
                    <div className="product-list-pending-actions">
                      <button type="button" className="product-list-btn-approve" disabled={busy} onClick={() => handleApprove(id)}>
                        {busy ? '…' : 'Approve'}
                      </button>
                      <button type="button" className="product-list-btn-reject" disabled={busy} onClick={() => handleReject(id)}>
                        Reject
                      </button>
                    </div>
                  ) : (
                    <p className="product-list-pending-wait">Waiting for owner approval.</p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : pendingOnly ? (
        <section className="product-list-pending">
          <h3 className="product-list-pending-title">Stock pending approvals</h3>
          <p className="product-list-empty product-list-empty--inline">No pending stock requests right now.</p>
        </section>
      ) : null}
    </div>
  );
};

export default ProductListPage;
